import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Lock,
  Shield,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  deriveSASFromSdps,
  markPeerVerified,
  getPeerVerification,
  recordPeerAttestedMe,
  mySasVerifiedCount90d,
} from '../../utils/voiceSAS';

// localStorage tombstone so the "First voice verified" celebratory
// toast fires exactly once per device, not on every re-install or
// panic-button reset. Shares the `ghostcomm_` prefix so the panic
// wipe takes it out with everything else.
const FIRST_SAS_CELEBRATED_KEY = 'ghostcomm_first_sas_celebrated';

export const VoiceCallModal = ({ isOpen, onClose, contact, isIncoming, onAnswer }) => {
  const { sendWsMessage, addWsListener, user } = useAuth();
  const [callState, setCallState] = useState(isIncoming ? 'incoming' : 'calling');
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionType, setConnectionType] = useState('connecting');
  // SAS (Short Authentication String) for spoken verification of
  // the media channel. Null until the peer connection finishes
  // negotiating DTLS; then one of 'NNN-NNN' | 'unsupported'.
  // `sasState`: 'pending' | 'match' | 'mismatch'.
  const [sasCode, setSasCode] = useState(null);
  const [sasState, setSasState] = useState('pending');
  // Prior verification row from localStorage ({sas, verifiedAt,
  // peerUsername} | null). Softens the prompt copy from "Verify
  // this call" to "Re-verify periodically" — it does NOT bypass
  // the match check because every call negotiates fresh DTLS
  // fingerprints, so stored codes don't match new calls by design.
  const [priorVerification, setPriorVerification] = useState(null);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const remoteAudio = useRef(null);
  const timerRef = useRef(null);
  // ICE candidates that arrive during the incoming-call offer window
  // (before the user clicks Answer and the real RTCPeerConnection
  // exists) must be queued — otherwise they're silently dropped and
  // the audio stream never negotiates, leaving a connected-but-silent
  // call. Flushed inside `answerCall` once `setRemoteDescription` has
  // accepted the offer.
  const pendingIceCandidates = useRef([]);

  // TURN server configuration for server-relayed calls (hides IP addresses).
  // We keep `iceTransportPolicy: 'all'` so STUN direct-connect can succeed
  // when both peers are reachable — the free openrelay TURN pool is under
  // heavy load and forcing `relay` there was routinely leaving calls
  // connected with no audio. When a TURN relay is available we still
  // prefer it (because the ICE agent naturally picks the highest-priority
  // working pair), but we no longer black-hole the call if TURN fails.
  const config = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      {
        urls: 'turn:openrelay.metered.ca:80',
        username: 'openrelayproject',
        credential: 'openrelayproject',
      },
      {
        urls: 'turn:openrelay.metered.ca:443',
        username: 'openrelayproject',
        credential: 'openrelayproject',
      },
      {
        urls: 'turn:openrelay.metered.ca:443?transport=tcp',
        username: 'openrelayproject',
        credential: 'openrelayproject',
      },
    ],
    iceTransportPolicy: 'all',
  };

  // Compute the SAS from the now-negotiated peer connection and
  // stash it on state. Called right after we've flipped to
  // 'connected' on either side. Safe to re-call (idempotent); if
  // either SDP is missing a sha-256 fingerprint we flag
  // 'unsupported' so the UI can explain why there's no code.
  // Declared BEFORE startCall/answerCall so their useCallback dep
  // arrays can reference it without hitting a TDZ ReferenceError.
  const computeSas = useCallback(async () => {
    const pc = peerConnection.current;
    const localSdp = pc?.localDescription?.sdp;
    const remoteSdp = pc?.remoteDescription?.sdp;
    if (!localSdp || !remoteSdp) return;
    try {
      const code = await deriveSASFromSdps({ localSdp, remoteSdp });
      setSasCode(code || 'unsupported');
    } catch (_e) {
      setSasCode('unsupported');
    }
  }, []);

  const startCall = useCallback(async () => {
    try {
      localStream.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      peerConnection.current = new RTCPeerConnection(config);

      // Monitor connection type
      peerConnection.current.oniceconnectionstatechange = () => {
        const state = peerConnection.current?.iceConnectionState;
        if (state === 'connected' || state === 'completed') {
          // Check if using relay
          peerConnection.current.getStats().then((stats) => {
            stats.forEach((report) => {
              if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                const isRelay = report.remoteCandidateId?.includes('relay') ||
                               report.localCandidateId?.includes('relay');
                setConnectionType(isRelay ? 'relay (server)' : 'direct');
              }
            });
          });
        }
      };

      localStream.current.getTracks().forEach((track) => {
        peerConnection.current.addTrack(track, localStream.current);
      });

      peerConnection.current.ontrack = (event) => {
        if (remoteAudio.current) {
          remoteAudio.current.srcObject = event.streams[0];
          remoteAudio.current.muted = false;
          remoteAudio.current.volume = 1.0;
          // Some browsers gate autoplay even for audio originating
          // from a user-gesture context — explicitly kick playback.
          const p = remoteAudio.current.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      };

      peerConnection.current.onicecandidate = (event) => {
        if (event.candidate) {
          sendWsMessage({
            type: 'call_signal',
            target_user_id: contact.id,
            signal_type: 'ice-candidate',
            signal_data: event.candidate,
          });
        }
      };

      const offer = await peerConnection.current.createOffer();
      await peerConnection.current.setLocalDescription(offer);

      sendWsMessage({
        type: 'call_start',
        target_user_id: contact.id,
      });

      sendWsMessage({
        type: 'call_signal',
        target_user_id: contact.id,
        signal_type: 'offer',
        signal_data: offer,
      });
    } catch (error) {
      toast.error('Failed to access microphone');
      onClose();
    }
  }, [contact, sendWsMessage, onClose]);

  const answerCall = useCallback(async (offer) => {
    try {
      localStream.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      peerConnection.current = new RTCPeerConnection(config);

      // Monitor connection type
      peerConnection.current.oniceconnectionstatechange = () => {
        const state = peerConnection.current?.iceConnectionState;
        if (state === 'connected' || state === 'completed') {
          peerConnection.current.getStats().then((stats) => {
            stats.forEach((report) => {
              if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                const isRelay = report.remoteCandidateId?.includes('relay') ||
                               report.localCandidateId?.includes('relay');
                setConnectionType(isRelay ? 'relay (server)' : 'direct');
              }
            });
          });
        }
      };

      localStream.current.getTracks().forEach((track) => {
        peerConnection.current.addTrack(track, localStream.current);
      });

      peerConnection.current.ontrack = (event) => {
        if (remoteAudio.current) {
          remoteAudio.current.srcObject = event.streams[0];
          remoteAudio.current.muted = false;
          remoteAudio.current.volume = 1.0;
          const p = remoteAudio.current.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      };

      peerConnection.current.onicecandidate = (event) => {
        if (event.candidate) {
          sendWsMessage({
            type: 'call_signal',
            target_user_id: contact.id,
            signal_type: 'ice-candidate',
            signal_data: event.candidate,
          });
        }
      };

      await peerConnection.current.setRemoteDescription(new RTCSessionDescription(offer));
      // Flush any ICE candidates that arrived before the real
      // peer-connection existed — dropping them here was why incoming
      // calls connected but never produced audio on some networks.
      for (const cand of pendingIceCandidates.current) {
        try {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(cand));
        } catch (_e) {
          // non-fatal — remote side will resend survivors
        }
      }
      pendingIceCandidates.current = [];
      const answer = await peerConnection.current.createAnswer();
      await peerConnection.current.setLocalDescription(answer);

      sendWsMessage({
        type: 'call_signal',
        target_user_id: contact.id,
        signal_type: 'answer',
        signal_data: answer,
      });

      setCallState('connected');
      // DTLS is fully negotiated by the time we land here (offer
      // + answer have both been applied); derive the SAS so the
      // caller + callee can read-and-compare on the audio channel.
      computeSas();
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error('Failed to access microphone');
      onClose();
    }
  }, [contact, sendWsMessage, onClose, computeSas]);

  useEffect(() => {
    if (!isIncoming && isOpen) {
      startCall();
    }
  }, [isOpen, isIncoming, startCall]);

  useEffect(() => {
    const unsubscribe = addWsListener(async (data) => {
      if (data.type === 'call_signal' && data.from_user_id === contact?.id) {
        if (data.signal_type === 'offer' && isIncoming) {
          // Store offer for when user answers
          peerConnection.current = { offer: data.signal_data };
        } else if (data.signal_type === 'answer') {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(data.signal_data));
          setCallState('connected');
          // Answer applied → DTLS is locked in. Compute SAS so the
          // caller sees the same code the callee is now rendering.
          computeSas();
          timerRef.current = setInterval(() => {
            setCallDuration((prev) => prev + 1);
          }, 1000);
        } else if (data.signal_type === 'ice-candidate') {
          // If the real RTCPeerConnection isn't live yet (user hasn't
          // clicked Answer), buffer the candidate so we can replay it
          // inside `answerCall`. Otherwise apply immediately.
          if (peerConnection.current?.addIceCandidate) {
            try {
              await peerConnection.current.addIceCandidate(new RTCIceCandidate(data.signal_data));
            } catch (_e) { /* ignore — remote will resend */ }
          } else if (peerConnection.current && 'offer' in peerConnection.current) {
            pendingIceCandidates.current.push(data.signal_data);
          }
        } else if (data.signal_type === 'sas_attest') {
          // The peer clicked "Codes match" on their side. Stash a
          // local tombstone so the Security Halo card can count
          // distinct peers who've attested us back.
          recordPeerAttestedMe(contact?.id, data.signal_data?.sas);
        }
      }
      if (data.type === 'call_ended' && data.from_user_id === contact?.id) {
        endCall();
      }
    });
    return unsubscribe;
  }, [addWsListener, contact, isIncoming, computeSas]);

  const endCall = useCallback(() => {
    localStream.current?.getTracks().forEach((track) => track.stop());
    peerConnection.current?.close?.();
    if (timerRef.current) clearInterval(timerRef.current);
    pendingIceCandidates.current = [];
    sendWsMessage({
      type: 'call_end',
      target_user_id: contact?.id,
    });
    onClose();
  }, [contact, sendWsMessage, onClose]);

  const handleAnswer = async () => {
    if (peerConnection.current?.offer) {
      await answerCall(peerConnection.current.offer);
    }
  };

  const toggleMute = () => {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // "3 days ago" / "4 hours ago" / "just now" — keeps the SAS
  // re-verify prompt concrete without pulling in date-fns.
  const formatRelativeTime = (ms) => {
    const d = Date.now() - ms;
    if (d < 60_000) return 'just now';
    if (d < 3600_000) return `${Math.round(d / 60_000)} min ago`;
    if (d < 86400_000) return `${Math.round(d / 3600_000)} hr ago`;
    return `${Math.round(d / 86400_000)} day${Math.round(d / 86400_000) === 1 ? '' : 's'} ago`;
  };

  // Load any prior verification ledger row for this peer on mount.
  // Duress + missing-peer + localStorage-disabled all resolve to
  // null inside the helper, so no branching needed here.
  useEffect(() => {
    setPriorVerification(contact?.id ? getPeerVerification(contact.id) : null);
  }, [contact?.id]);

  const handleSasMatch = useCallback(() => {
    if (!sasCode || sasCode === 'unsupported') return;
    // Snapshot the count BEFORE we write the new tombstone so we
    // can tell whether this verification is the user's first-ever
    // inside the 90d window. Separate from the per-device
    // "first celebrated" flag below because even a user who has
    // never clicked "Codes match" before should only see the
    // celebration once, never on the 2nd / 3rd / etc. call.
    const priorCount = mySasVerifiedCount90d();
    markPeerVerified(contact?.id, sasCode, contact?.username);
    setSasState('match');
    setPriorVerification({
      sas: sasCode,
      verifiedAt: Date.now(),
      peerUsername: contact?.username || null,
    });
    // Tell the peer we attested. The `call_signal` relay accepts
    // arbitrary signal_types — zero backend change. Peer's WS
    // listener will call `recordPeerAttestedMe` on receipt so the
    // Security Halo card can count mutual verifications.
    try {
      sendWsMessage({
        type: 'call_signal',
        target_user_id: contact?.id,
        signal_type: 'sas_attest',
        signal_data: { sas: sasCode },
      });
    } catch {
      /* non-fatal — the attestation is still recorded locally */
    }
    // First-ever verification celebration. Fires once per device
    // (guarded by the localStorage flag). Opens Settings → Security
    // Halo via the custom-event deep link wired in Dashboard.js.
    // Skip in duress — a coerced session should never signal "you
    // just levelled up" because the coercer must not realise the
    // account has a working trust-verification ritual at all.
    let celebrated = false;
    try {
      const alreadyCelebrated = localStorage.getItem(FIRST_SAS_CELEBRATED_KEY);
      const inDuress = sessionStorage.getItem('gs_duress_mode') === '1';
      if (priorCount === 0 && !alreadyCelebrated && !inDuress) {
        localStorage.setItem(FIRST_SAS_CELEBRATED_KEY, String(Date.now()));
        celebrated = true;
      }
    } catch {
      /* storage disabled — fall back to standard toast */
    }
    if (celebrated) {
      toast.success('First voice verified — your halo just levelled up 🎙', {
        duration: 6000,
        action: {
          label: 'See halo',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('gs:open-security-halo'));
          },
        },
      });
    } else {
      toast.success('Call verified — no man-in-the-middle detected');
    }
  }, [sasCode, contact, sendWsMessage]);

  const handleSasMismatch = useCallback(() => {
    setSasState('mismatch');
    // Deliberately does NOT persist anything — a mismatch means we
    // do NOT trust this session, so we must not leave a "verified"
    // tombstone. The red banner + explicit "End call" button give
    // the user agency to decide what to do next (maybe confirm
    // over another channel first).
    toast.error('Codes do not match — end the call immediately if unexpected');
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <audio ref={remoteAudio} autoPlay playsInline />
      <div className="bg-card p-8 rounded-2xl border border-border text-center max-w-sm w-full mx-4">
        <div className="relative inline-block mb-6">
          <Avatar className="h-24 w-24">
            <AvatarFallback className="bg-primary/20 text-primary text-2xl">
              {contact?.username?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {callState === 'calling' && <div className="pulse-ring absolute inset-0 rounded-full" />}
        </div>

        <h3 className="text-xl font-semibold text-white mb-2">{contact?.username}</h3>
        <p className="text-slate-400 mb-6">
          {callState === 'incoming' && 'Incoming call...'}
          {callState === 'calling' && 'Calling...'}
          {callState === 'connected' && formatDuration(callDuration)}
        </p>

        <div className="flex items-center justify-center gap-4">
          {callState === 'incoming' && (
            <Button
              onClick={handleAnswer}
              className="h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="answer-call-btn"
            >
              <Phone className="w-6 h-6" />
            </Button>
          )}
          {callState === 'connected' && (
            <Button
              variant="secondary"
              onClick={toggleMute}
              className={`h-14 w-14 rounded-full ${isMuted ? 'bg-red-600 hover:bg-red-700' : ''}`}
              data-testid="mute-call-btn"
            >
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>
          )}
          <Button
            onClick={endCall}
            className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-700"
            data-testid="end-call-btn"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>

        <div className="mt-6 space-y-2">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Lock className="w-3 h-3" />
            <span>End-to-end encrypted</span>
          </div>
          {callState === 'connected' && (
            <div className="flex items-center justify-center gap-2 text-xs text-emerald-400">
              <Shield className="w-3 h-3" />
              <span>Routed via: {connectionType}</span>
            </div>
          )}
        </div>

        {callState === 'connected' && sasCode && (
          <div
            className="mt-5 pt-5 border-t border-border/50"
            data-testid="sas-verification-block"
            data-sas-state={sasState}
          >
            {sasCode === 'unsupported' ? (
              <p className="text-xs text-slate-500 text-center">
                Voice verification unavailable on this connection.
              </p>
            ) : sasState === 'match' ? (
              <div
                className="flex items-center justify-center gap-2 text-sm text-emerald-400"
                data-testid="sas-verified-badge"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Call verified · <span className="font-mono">{sasCode}</span></span>
              </div>
            ) : sasState === 'mismatch' ? (
              <div
                className="space-y-3"
                data-testid="sas-mismatch-warning"
              >
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs leading-relaxed">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-rose-200">Codes do not match.</strong> This call may be intercepted. End the call and confirm with your peer through another channel before sharing anything sensitive.
                  </span>
                </div>
                <Button
                  onClick={endCall}
                  variant="destructive"
                  className="w-full h-10"
                  data-testid="sas-mismatch-end-btn"
                >
                  End call now
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 text-center">
                  {priorVerification ? 'Re-verify periodically' : 'Say this code aloud to verify'}
                </p>
                <div
                  className="font-mono text-3xl tracking-[0.25em] text-center text-primary select-all"
                  data-testid="sas-code-display"
                >
                  {sasCode}
                </div>
                <p className="text-xs text-slate-400 text-center leading-relaxed">
                  {priorVerification
                    ? `You last verified ${formatRelativeTime(priorVerification.verifiedAt)}. Fresh codes on every call — compare out loud if anything feels off.`
                    : 'If the code your peer reads does NOT match the one above, someone is listening in.'}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSasMatch}
                    className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="sas-match-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Codes match
                  </Button>
                  <Button
                    onClick={handleSasMismatch}
                    variant="outline"
                    className="flex-1 h-9 border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    data-testid="sas-mismatch-btn"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Don't match
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
